/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	CosmosAsset,
	CosmosCarousel,
	OptimizedCosmosAssetFragment,
	ResponsiveContainer,
	SectionContainer,
	SectionHeading,
	BackgroundContainer,
} from "@components/elements";
import { AssetCarouselSectionFragment } from "@cosmos/hygraph-schema";
import { extractBackgroundContainerProps, extractSectionContainerProps } from "@components/utils/section.utils";
import { css, cx } from "@panda/css";
import { VStack } from "@panda/jsx";

export interface AssetCarouselSectionProps extends AssetCarouselSectionFragment {
	className?: string;
}

const slideAssetStyle = css({
	height: "100%",
	width: "100%",
	objectFit: "cover",
	borderRadius: "eds.borderRadius.24",
	smDown: {
		borderRadius: "eds.borderRadius.12",
	},
});

export const AssetCarouselSection = ({
	className,
	config,
	assets,
	caption,
}: AssetCarouselSectionProps): React.JSX.Element | null => {
	const validHeading = Boolean(config?.heading?.title || config?.heading?.desc);
	const validContent = Boolean(assets?.length);
	if (!validHeading && !validContent) return null;
	return (
		<BackgroundContainer {...extractBackgroundContainerProps(config)}>
			<ResponsiveContainer>
				<SectionContainer {...extractSectionContainerProps(config)}>
					<VStack alignItems="stretch" gap="eds.dimension.96" className={cx("asset-carousel-section", className)}>
						{validHeading ? <SectionHeading {...config?.heading} /> : null}
						{validContent ? (
							<CosmosCarousel
								loop={true}
								buttonPosition="inside"
								dots={true}
								slideSpacing="0"
								carouselType="scaled"
								slides={assets.map((asset: OptimizedCosmosAssetFragment) => (
									<CosmosAsset className={slideAssetStyle} asset={asset} key={asset.id} />
								))}
								caption={caption}
							/>
						) : null}
					</VStack>
				</SectionContainer>
			</ResponsiveContainer>
		</BackgroundContainer>
	);
};
